// ConfirmUnlinkDialog — reusable confirmation dialog for unlinking cross-references.
// Built on shadcn AlertDialog. Captures optional notes about why the link is being removed.
// Notes are written to the owning object's audit history by the backend.

import { useState } from 'react'
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogCancel,
} from '@/components/ui/alert-dialog'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'

interface ConfirmUnlinkDialogProps {
  open: boolean
  onClose: () => void
  onConfirm: (notes: string) => void
  title: string
  description: string
  isPending: boolean
  confirmLabel?: string
}

export function ConfirmUnlinkDialog({
  open,
  onClose,
  onConfirm,
  title,
  description,
  isPending,
  confirmLabel = 'Remove Link',
}: ConfirmUnlinkDialogProps) {
  const [notes, setNotes] = useState('')

  function handleConfirm() {
    onConfirm(notes.trim())
  }

  function handleClose() {
    if (!isPending) {
      setNotes('')
      onClose()
    }
  }

  return (
    <AlertDialog open={open} onOpenChange={(v) => !v && handleClose()}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{title}</AlertDialogTitle>
          <AlertDialogDescription>{description}</AlertDialogDescription>
        </AlertDialogHeader>

        <div className="space-y-1.5 py-2">
          <Label htmlFor="unlink-notes">Reason (optional)</Label>
          <textarea
            id="unlink-notes"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Why is this link being removed?"
            disabled={isPending}
            className="min-h-[60px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:opacity-50"
          />
        </div>

        <AlertDialogFooter>
          <AlertDialogCancel disabled={isPending} onClick={handleClose}>
            Cancel
          </AlertDialogCancel>
          <Button
            variant="destructive"
            onClick={handleConfirm}
            disabled={isPending}
          >
            {isPending ? 'Removing...' : confirmLabel}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}
